import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Page,
  Layout,
  Card,
  EmptyState,
  IndexTable,
  Text,
  Badge,
} from "@shopify/polaris";
import { useAuthenticatedFetch } from "../hooks/useAuthenticatedFetch";

interface Vendor {
  id: string;
  name: string;
  code: string | null;
  email: string | null;
  isActive: boolean;
  productCount?: number;
}

export default function VendorList() {
  const authFetch = useAuthenticatedFetch();
  const navigate = useNavigate();
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);

  const loadVendors = useCallback(async () => {
    try {
      setLoading(true);
      const res = await authFetch("/api/vendors");
      const json = await res.json();
      setVendors(json.data || []);
    } catch (error) {
      console.error("Failed to load vendors:", error);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    loadVendors();
  }, []);

  const resourceName = {
    singular: "vendor",
    plural: "vendors",
  };

  const rowMarkup = vendors.map((vendor, index) => (
    <IndexTable.Row
      id={vendor.id}
      key={vendor.id}
      selected={false}
      position={index}
      onClick={() => navigate(`/vendors/${vendor.id}`)}
    >
      <IndexTable.Cell>
        <Text variant="bodyMd" fontWeight="bold" as="span">
          {vendor.name}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>{vendor.code || "\u2014"}</IndexTable.Cell>
      <IndexTable.Cell>{vendor.email || "\u2014"}</IndexTable.Cell>
      <IndexTable.Cell>
        <Badge tone={vendor.isActive ? "success" : undefined}>
          {vendor.isActive ? "Active" : "Inactive"}
        </Badge>
      </IndexTable.Cell>
      <IndexTable.Cell>{vendor.productCount ?? "\u2014"}</IndexTable.Cell>
      <IndexTable.Cell>{"\u2014"}</IndexTable.Cell>
    </IndexTable.Row>
  ));

  const emptyStateMarkup = (
    <EmptyState
      heading="No vendors yet"
      action={{ content: "Add Vendor", url: "/vendors/new" }}
      image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
    >
      <p>
        Add your first vendor to start creating purchase orders. Vendor names
        must match the vendor field on your Shopify products exactly.
      </p>
    </EmptyState>
  );

  return (
    <Page
      title="Purchase Orders"
      subtitle="Select a vendor to view products and create purchase orders"
      primaryAction={{ content: "Add Vendor", url: "/vendors/new" }}
      secondaryActions={[
        { content: "All Purchase Orders", url: "/purchase-orders" },
        { content: "Settings", url: "/settings" },
      ]}
    >
      <Layout>
        <Layout.Section>
          {vendors.length === 0 && !loading ? (
            <Card>{emptyStateMarkup}</Card>
          ) : (
            <Card padding="0">
              <IndexTable
                resourceName={resourceName}
                itemCount={vendors.length}
                selectedItemsCount={0}
                onSelectionChange={() => {}}
                headings={[
                  { title: "Vendor Name" },
                  { title: "Code" },
                  { title: "Email" },
                  { title: "Status" },
                  { title: "Products" },
                  { title: "Open POs" },
                ]}
                loading={loading}
                selectable={false}
              >
                {rowMarkup}
              </IndexTable>
            </Card>
          )}
        </Layout.Section>
      </Layout>
    </Page>
  );
}
